import {useCallback, useEffect, useState} from "react";
import {Bell} from "lucide-react";
import {
  deleteAdminClientNotification,
  listAdminClientNotifications,
  listAdminProducts,
  updateAdminClientNotification,
  type AdminClientNotification
} from "@zaaaz/shared";
import {useAdminConfirm} from "@/hooks/useAdminConfirm";
import {useAdminSectionBadges} from "@/hooks/useAdminSectionBadges";
import {ADMIN_SECTION_NAV} from "@/lib/adminSectionNav";
import {adminSearchText} from "@/lib/adminSearchText";
import {adminToast} from "@/lib/adminToast";
import {Button} from "@/components/ui/button";
import {Input} from "@/components/ui/input";
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from "@/components/ui/select";
import {AdminAlert, AdminBadge, AdminEditModal, AdminField, AdminFormActions, AdminModernTable, AdminPageLayout, AdminPagination, AdminSectionTabs} from "./ui";

export function ClientNotificationsView() {
  const {confirm, dialog: confirmDialog} = useAdminConfirm();
  const badges = useAdminSectionBadges("clienti");
  const [items, setItems] = useState<AdminClientNotification[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [openId, setOpenId] = useState<number | null>(null);
  const [form, setForm] = useState({email: "", productId: 0});
  const [productOpts, setProductOpts] = useState<{id: number; label: string}[]>([]);
  const [err, setErr] = useState("");
  const load = useCallback(() => {
    listAdminClientNotifications({page, pageSize: 20, search: searchQuery || undefined})
      .then((r) => { setItems(r.items); setTotalPages(r.totalPages); })
      .catch((e: Error) => setErr(e.message));
  }, [page, searchQuery]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    listAdminProducts({page: 1, pageSize: 100}).then((r) => {
      setProductOpts(r.items.map((p) => ({id: p.id, label: `${p.cod} ${p.nume}`})));
    }).catch(() => {});
  }, []);

  const commitSearch = useCallback(() => {
    setSearchQuery(search);
    setPage(1);
  }, [search]);

  function closeModal(open: boolean) {
    setModalOpen(open);
    if(!open) setOpenId(null);
  }
  function openItem(n: AdminClientNotification) {
    setOpenId(n.id);
    setForm({email: n.email, productId: n.productId});
    setModalOpen(true);
  }
  async function saveItem() {
    if(typeof openId !== "number") return;
    try {
      await updateAdminClientNotification(openId, form);
      adminToast.success("Notification saved.");
      closeModal(false);
      load();
    } catch(e) {
      adminToast.error(e instanceof Error ? e.message : "Save failed");
    }
  }
  function removeItem(id: number) {
    confirm("Delete notification?", async () => {
      await deleteAdminClientNotification(id);
      closeModal(false);
      load();
      adminToast.success("Notification deleted.");
    });
  }
  if(err) return <AdminAlert variant="error">{err}</AdminAlert>;
  return (
    <AdminPageLayout
      title={ADMIN_SECTION_NAV.clienti.title}
      description="Stock notifications for clients"
      breadcrumbs={[
        {label: ADMIN_SECTION_NAV.clienti.title, href: "/vadmin/clienti"},
        {label: "Notifications"}
      ]}
    >
      {confirmDialog}
      <AdminSectionTabs section="clienti" badges={badges} />
      <AdminModernTable
        items={items}
        getId={(n) => n.id}
        getTitle={(n) => n.email}
        getSubtitle={(n) => n.productName}
        getMeta={(n) => (
          <>
            {n.guest ? <AdminBadge variant="secondary">Guest</AdminBadge> : null}
          </>
        )}
        getSearchText={(n) => adminSearchText(n.id, n.email, n.productName, n.productId, n.clientId, n.guest)}
        searchValue={search}
        onSearchChange={setSearch}
        onSearchCommit={commitSearch}
        clientFilter={false}
        selectedId={openId}
        onRowClick={openItem}
        emptyTitle="No notifications"
        emptyDescription="Adjust filters or check back later."
        searchPlaceholder="Search email, produs..."
      />
      <AdminEditModal
        open={modalOpen}
        onOpenChange={closeModal}
        title={openId ? `Notification #${openId}` : "Notification"}
        icon={Bell}
        footer={(
          <AdminFormActions>
            <Button type="button" size="sm" onClick={() => saveItem()}>Save</Button>
            {typeof openId === "number" ? (
              <Button type="button" variant="outline" size="sm" onClick={() => removeItem(openId)}>Delete</Button>
            ) : null}
          </AdminFormActions>
        )}
      >
        <div className="admin-detail-panel__form">
          <AdminField label="Email">
            <Input value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} placeholder="Email" />
          </AdminField>
          <AdminField label="Product">
            <Select value={String(form.productId)} onValueChange={(v) => setForm({...form, productId: Number(v)})}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {productOpts.map((p) => <SelectItem key={p.id} value={String(p.id)}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </AdminField>
        </div>
      </AdminEditModal>
      <AdminPagination page={page} totalPages={totalPages} onPrev={() => setPage(page - 1)} onNext={() => setPage(page + 1)} />
    </AdminPageLayout>
  );
}
